/**
 * AppOrdersPage.tsx
 *
 * Every payment that bought a product, with the category of what was bought and
 * which client took the payment.
 *
 * ── Payments, not integration orders ────────────────────────────────────────
 * "A product bought" lives in a payment's line items. That is the general case:
 * an IV Therapy or Vitamin Shot sale has line items and no supplier order behind
 * it at all. An earlier build of this page listed integration orders instead,
 * which showed only the small supplier-brokered slice — on the busiest account,
 * ~100 orders against ~182,000 product payments.
 *
 * ── Category needs no new data ──────────────────────────────────────────────
 * The category comes from the practice's own product catalog, through the line
 * item's catalog entry — the same vocabulary the Payments screen uses, already
 * populated (IV Therapy, Vitamin Shots, Peptides, Labs, Consultations …). A
 * payment can carry several products from several categories, so the cell lists
 * each one, and the filter matches a payment if ANY line item is in that
 * category: narrowing to payments that bought ONLY that category would drop
 * mixed baskets, which are most of them.
 *
 * ── Source is absent, not 'webapp' ──────────────────────────────────────────
 * A payment with no `api_request_metadata` row was taken before that capture
 * shipped — which is nearly all of them, and will stay that way for the history.
 * It renders as an em dash and is counted under its own tile. It is never
 * relabelled as a channel: this data is meant to show where selling actually
 * happens, and a guess is indistinguishable from an observation once written.
 *
 * ── Why counts are fetched separately from pages ────────────────────────────
 * The tiles and the row total need a grouped scan over every matching payment
 * (~1.5s on the busiest account). None of those figures change when you turn a
 * page, so `withCounts` is sent on first load and on filter changes only. That
 * is the difference between a 0.4s page turn and a 1.9s one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ExternalLink, ShoppingBag, Syringe } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Pagination } from '@/components/ui/Pagination';
import { PatientLink } from '@/components/ui/PatientLink';
import { Button } from '@/components/ui/Button';
import { PaymentStatusBadge } from '@/components/payments/PaymentStatusBadge';
import { useEffectiveFacilityIds, useIsFacilityLocked } from '@/hooks/useGlobalFacility';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import { getAppOrders, listProductCategories, type AppOrderRow, type AppOrderSourceCounts } from '@/services/payments.service';
import { toast } from '@/store/toastStore';
import { listFacilities } from '@/services/admin.service';

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search patient, payment #, product…' },
  // multi + searchable render the shared checklist with its own search box —
  // this account has 50+ categories, and a plain dropdown is unusable at that
  // size. Both flags are FilterConfig's; nothing bespoke is built here.
  { key: 'category', label: 'Category', type: 'select', allLabel: 'All categories', multi: true, searchable: true },
  // multi, like Category: "everything bought in an app" is mobile OR webapp
  // OR nexus, and a single-choice Source made that three separate passes with
  // no way to see the union.
  { key: 'source', label: 'Source', type: 'select', allLabel: 'Any source', multi: true },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
  // SCRUM-1868: declined and refunded payments used to render identically
  // (grayed out, struck through) — same status vocabulary as the Payments
  // list so this filters and reads consistently with it.
  { key: 'status', label: 'Payment Status', type: 'select', allLabel: 'Any status', multi: true },
  // SUP-000744: the queue staff actually work — peptide payments not yet sent
  // to the pharmacy.
  { key: 'fulfillment', label: 'Fulfillment', type: 'select', allLabel: 'Any' },
  { key: 'daterange', label: 'Order Date', type: 'daterange' },
];

const FULFILLMENT_OPTIONS: FilterOption[] = [
  { value: 'needs', label: 'Needs fulfillment' },
  { value: 'fulfilled', label: 'Fulfilled' },
];

/**
 * What the row offers (SUP-000744). Fulfill appeared on every collected
 * peptide payment, including ones already sent to the pharmacy — fulfilling
 * those created a second order, then failed to link the already-spent payment.
 *
 *  - 'fulfilled': a live peptide order was paid with this payment;
 *  - 'fulfill'  : a peptide payment, collected, with no live order (an order
 *                 that was cancelled leaves it needing fulfilment again);
 *  - null       : nothing to fulfil (not peptides, voided, or a refund row).
 */
export function fulfillmentAction(r: Pick<AppOrderRow, 'isPeptide' | 'void' | 'amount' | 'fulfillment'>): 'fulfilled' | 'fulfill' | null {
  if (r.fulfillment?.live) return 'fulfilled';
  if (r.isPeptide && !r.void && r.amount > 0) return 'fulfill';
  return null;
}

/** Same key→label vocabulary as PaymentStatusBadge/getPaymentStatusDisplay. */
const STATUS_OPTIONS: FilterOption[] = [
  { value: 'settled', label: 'Settled' },
  { value: 'pending', label: 'Pending' },
  { value: 'declined', label: 'Declined' },
  { value: 'refund', label: 'Refund' },
  { value: 'voided', label: 'Voided' },
  { value: 'manually_entered', label: 'Manual' },
];

/** See PeptidesPage — sentinel so payments with no facility stay reachable. */
const NO_FACILITY = 'none';

const SOURCE_LABELS: Record<string, string> = {
  webapp: 'Web App',
  // Split rather than one "Mobile App": separate stores, separate release
  // trains, separate ad platforms — a combined figure answers no question
  // anyone actually asks about them.
  ios: 'iOS',
  android: 'Android',
  nexus: 'Health Nexus',
};


/**
 * Only the tagged channels. Untagged is not an option because it is not part
 * of this grid: the server excludes it, so offering it would only produce an
 * empty list. The payments it covers are still in All Payments, where they
 * belong — this page answers where selling happens, and they cannot.
 */
const SOURCE_OPTIONS: FilterOption[] = Object.entries(SOURCE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

const PER_PAGE = 50;

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

export default function AppOrdersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  /**
   * Chosen categories. `null` means never chosen — the grid then defaults to
   * this account's peptide categories, because the page exists to show
   * PRODUCTS bought and the catalog is ~95% services (IV therapy, vitamin
   * shots, injectables, labs, memberships). An empty array is a real choice —
   * "All categories" — and must not snap back to the default, which is why
   * "not chosen" and "chose everything" are different values here.
   */
  const [storedCategories, setStoredCategories] = useLocalStorage<string[] | null>(
    'ftp-app-orders-categories',
    null,
  );
  const [sources, setSources] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [fulfillmentFilter, setFulfillmentFilter] = useState<'' | 'needs' | 'fulfilled'>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [facility, setFacility] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const { data: categories = [] } = useQuery({
    queryKey: ['product-categories'],
    queryFn: listProductCategories,
    staleTime: 5 * 60_000,
  });
  const { data: facilities = [] } = useQuery({
    queryKey: ['admin-facilities'],
    queryFn: listFacilities,
    staleTime: 5 * 60_000,
  });

  /**
   * Categories whose title mentions peptides — the default selection.
   *
   * A title match rather than a hardcoded id: category ids differ per account
   * (Next Health's Peptides is 127), and the grid has to open on something
   * sensible for an account nobody has configured. It only ever seeds the
   * FIRST visit; after that the stored choice wins, including a deliberate
   * "all categories".
   */
  const peptideCategoryIds = useMemo(
    () => categories.filter((c) => /peptide/i.test(c.title)).map((c) => String(c.quoteItemCostsCategoryID)),
    [categories],
  );

  /**
   * Seed the default once, and only once the category list has actually
   * arrived — seeding from an empty list would write "all categories" as
   * though the user had chosen it.
   */
  useEffect(() => {
    if (storedCategories === null && categories.length > 0) {
      setStoredCategories(peptideCategoryIds);
    }
  }, [storedCategories, categories.length, peptideCategoryIds, setStoredCategories]);

  const selectedCategories = storedCategories ?? peptideCategoryIds;

  const isFacilityLocked = useIsFacilityLocked();
  const globalFacilityIds = useEffectiveFacilityIds(facility);
  const effectiveFacility = useMemo<string[]>(
    () => (isFacilityLocked ? [...globalFacilityIds, NO_FACILITY] : facility),
    [isFacilityLocked, globalFacilityIds, facility],
  );

  // Everything except `page`. Changing any of it invalidates the counts and
  // sends the user back to the first page; turning a page does neither.
  const filters = useMemo(() => ({
    search: search.trim() || undefined,
    categoryIds: selectedCategories.length ? selectedCategories.map(Number) : undefined,
    sources: sources.length ? sources : undefined,
    statuses: statuses.length ? statuses : undefined,
    fulfillment: fulfillmentFilter || undefined,
    facilityIds: effectiveFacility.length ? effectiveFacility : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  }), [search, selectedCategories, sources, statuses, fulfillmentFilter, effectiveFacility, dateFrom, dateTo]);

  const filterKey = JSON.stringify(filters);
  useEffect(() => { setPage(1); }, [filterKey]);

  const { data, isFetching } = useQuery({
    queryKey: ['app-orders', filterKey, page],
    queryFn: () => getAppOrders({ ...filters, page, perPage: PER_PAGE, withCounts: true }),
    // Rows stay on screen while the next page loads, so paging does not flash
    // an empty table on a query this size.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  /**
   * The last counts we were given. The server omits them on any request that
   * did not ask, so holding the previous set keeps the tiles and the pager
   * steady instead of blanking mid-page-turn.
   */
  const lastCounts = useRef<AppOrderSourceCounts | null>(null);
  if (data?.meta.sourceCounts) lastCounts.current = data.meta.sourceCounts;
  const counts = data?.meta.sourceCounts ?? lastCounts.current;
  const total = data?.meta.total ?? counts?.total ?? 0;

  const rows = data?.data ?? [];

  const categoryOptions = useMemo<FilterOption[]>(
    () => categories.map((c) => ({ value: String(c.quoteItemCostsCategoryID), label: c.title })),
    [categories],
  );
  const facilityOptions = useMemo<FilterOption[]>(() => ([
    { value: NO_FACILITY, label: 'No facility' },
    ...facilities.filter((f) => f.active).map((f) => ({
      value: String(f.facilityId),
      label: f.facilityName || 'Unknown',
    })),
  ]), [facilities]);

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({ category: categoryOptions, source: SOURCE_OPTIONS, status: STATUS_OPTIONS, fulfillment: FULFILLMENT_OPTIONS, facility: facilityOptions }),
    [categoryOptions, facilityOptions],
  );
  const sidebarValues = useMemo<Record<string, FilterValue>>(() => ({
    search: search ? [search] : [],
    category: selectedCategories,
    source: sources,
    status: statuses,
    fulfillment: fulfillmentFilter ? [fulfillmentFilter] : [],
    facility: effectiveFacility,
    daterange: { from: dateFrom, to: dateTo },
  }), [search, selectedCategories, sources, statuses, fulfillmentFilter, effectiveFacility, dateFrom, dateTo]);

  const handleSidebarChange = useCallback((key: string, value: FilterValue) => {
    switch (key) {
      case 'search': if (Array.isArray(value)) setSearch(value[0] ?? ''); break;
      case 'category': if (Array.isArray(value)) setStoredCategories(value as string[]); break;
      case 'source': if (Array.isArray(value)) setSources(value as string[]); break;
      case 'status': if (Array.isArray(value)) setStatuses(value as string[]); break;
      case 'fulfillment':
        if (Array.isArray(value)) {
          const v = value[0];
          setFulfillmentFilter(v === 'needs' || v === 'fulfilled' ? v : '');
        }
        break;
      case 'facility': if (Array.isArray(value)) setFacility(value as string[]); break;
      case 'daterange':
        if (!Array.isArray(value) && 'from' in value) { setDateFrom(value.from || ''); setDateTo(value.to || ''); }
        break;
    }
  }, [setStoredCategories]);

  const handleSidebarClear = useCallback((key: string) => {
    switch (key) {
      case 'search': setSearch(''); break;
      // [] not null: clearing is a deliberate "all categories", and must
      // not be re-seeded with the peptide default on the next render.
      case 'category': setStoredCategories([]); break;
      case 'status': setStatuses([]); break;
      case 'fulfillment': setFulfillmentFilter(''); break;
      case 'source': setSources([]); break;
      case 'facility': setFacility([]); break;
      case 'daterange': setDateFrom(''); setDateTo(''); break;
    }
  }, [setStoredCategories]);

  /**
   * Fulfil a peptide payment: go to Peptides and open the Axiom order modal for
   * that patient, with what they paid for shown beside the catalog.
   *
   * The paid items travel in router state rather than the URL — they are a
   * display aid, not addressable content, and a dozen product names do not
   * belong in a query string. `fulfillPatient` stays in the URL so a refresh
   * still opens the right patient's order, just without the reference panel.
   */
  const fulfill = useCallback(async (r: AppOrderRow) => {
    // Re-check before opening a new order: the grid can be minutes old, and
    // another tab (or person) may have fulfilled this payment since. A second
    // order here is the duplicate SUP-000744 reported — and it could not even
    // be paid, because the payment is already spent on the first.
    try {
      const fresh = await getAppOrders({ search: String(r.paymentID), fulfillment: 'fulfilled', perPage: 5 });
      const done = fresh.data.find((x) => x.paymentID === r.paymentID && x.fulfillment?.live);
      if (done?.fulfillment) {
        toast.info(`Payment #${r.paymentID} was already fulfilled — order #${done.fulfillment.orderId}.`);
        navigate(`/patients/${r.patientID}?section=peptides`);
        return;
      }
    } catch {
      // The check is a guard, not a gate: if it can't run, the server still
      // refuses to spend the payment twice when the new order is paid.
    }
    navigate(`/peptides?fulfillPatient=${r.patientID}`, {
      state: {
        fulfillPayment: {
          paymentId: r.paymentID,
          paidOn: r.dtmDate,
          patientName: r.patientName,
          items: r.paidItems.map((i) => ({ name: i.name, quantity: i.quantity, amount: i.amount })),
        },
      },
    });
  }, [navigate]);

  // The category default counts as an active filter: with it on, an empty grid
  // means "no peptide purchases", not "no product payments at all", and the
  // empty state has to say the thing that points at the filter.
  const hasActiveFilters = !!search || selectedCategories.length > 0 || sources.length > 0
    || statuses.length > 0 || !!fulfillmentFilter || !!dateFrom || !!dateTo || effectiveFacility.length > 0;

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={FILTER_CONFIGS}
          values={sidebarValues}
          options={sidebarOptions}
          onChange={handleSidebarChange}
          onClear={handleSidebarClear}
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          searchCommitMode="change"
          showAIAssistant={false}
          storageKey="app-orders"
        />
      )}
    >
      <div className="flex h-full min-h-0 flex-col max-md:h-auto">
        <div className="shrink-0 space-y-6 p-6">
          <PageHeader
            variant="compact"
            title="App Orders"
            subtitle="Payments for a product bought, by category and by the app the payment came from"
          />

          {/* Labelled for tests: "Mobile App" and friends also appear as filter
              options and as cell values, so assertions have to scope here. */}
          <MetricCardGrid columns={5} data-testid="app-orders-kpis">
            <StatCard label="Payments" value={counts?.total ?? 0} tone="blue" />
            <StatCard label="Web App" value={counts?.webapp ?? 0} tone="sky" />
            <StatCard label="iOS" value={counts?.ios ?? 0} tone="violet" />
            <StatCard label="Android" value={counts?.android ?? 0} tone="violet" />
            <StatCard label="Health Nexus" value={counts?.nexus ?? 0} tone="emerald" />

          </MetricCardGrid>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-6 pb-6 max-md:overflow-visible">
          {rows.length === 0 && !isFetching ? (
            <div className="py-12 text-center">
              <ShoppingBag className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">
                {hasActiveFilters ? 'No payments match your filters.' : 'No product payments yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Patient</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2">Category</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Source</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className={isFetching ? 'opacity-60 transition-opacity' : undefined}>
                    {rows.map((r) => (
                      <tr key={r.paymentID} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                          {r.dtmDate ? new Date(r.dtmDate).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {r.patientID && r.patientName ? (
                            <PatientLink
                              patientId={r.patientID}
                              patientName={r.patientName}
                              className="text-blue-600 hover:underline"
                            >
                              {r.patientName}
                            </PatientLink>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{r.products || '—'}</td>
                        <td className="px-3 py-2 text-slate-600">{r.categories || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">
                          {/* A voided sale still happened in some app, so it is
                              shown and struck through rather than hidden — the
                              channel split would be wrong without it. Links
                              straight to the payment (SCRUM-1868) rather than
                              just the patient's profile, same as the amount
                              the Payments list itself links from. */}
                          <Link
                            to={`/patients/${r.patientID}?section=payments&paymentId=${r.paymentID}`}
                            className={
                              r.void
                                ? 'text-slate-400 line-through hover:text-slate-500'
                                : 'text-blue-600 hover:underline'
                            }
                            title={`Payment #${r.paymentID}`}
                          >
                            {money(r.amount)}
                          </Link>
                        </td>
                        <td className="px-3 py-2">
                          {/* SCRUM-1868: a declined payment and a refunded one
                              used to look identical here (both just void +
                              struck-through amount) — same badge the Payments
                              list uses, so the two are finally distinguishable. */}
                          <PaymentStatusBadge payment={{ void: r.void, transStatus: r.transStatus }} />
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {/* Em dash for untagged. NOT defaulted to a channel. */}
                          {r.source ? (SOURCE_LABELS[r.source] ?? r.source) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {/* Only on a payment that was actually collected. A
                              voided payment is money given back, and a zero or
                              negative row is a refund or an adjustment —
                              fulfilling either one ships peptides against
                              nothing. Voided rows stay VISIBLE, struck through;
                              it is the action that goes, not the row. */}
                          {fulfillmentAction(r) === 'fulfill' && (
                            <div className="flex flex-col items-end gap-0.5">
                              <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<Syringe className="h-3.5 w-3.5" />}
                                onClick={() => void fulfill(r)}
                              >
                                Fulfill
                              </Button>
                              {r.fulfillment && !r.fulfillment.live && (
                                <span className="text-[11px] text-slate-500">Order #{r.fulfillment.orderId} was cancelled</span>
                              )}
                            </div>
                          )}
                          {fulfillmentAction(r) === 'fulfilled' && r.fulfillment && (
                            <Link
                              to={`/patients/${r.patientID}?section=peptides`}
                              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                              title={`Fulfilled by order #${r.fulfillment.orderId}${r.fulfillment.status ? ` (${r.fulfillment.status})` : ''}`}
                            >
                              Fulfilled · #{r.fulfillment.orderId}
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {total > PER_PAGE && (
                <Pagination
                  className="mt-4"
                  total={total}
                  page={page}
                  pageSize={PER_PAGE}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </ListPageLayout>
  );
}
